import React, { useContext, useEffect, useMemo, useState } from 'react';
// import { Dropdown, Row, Tab, Tabs } from 'react-bootstrap';
import Procedural from '@components/advancement/Procedural';
import Financial from '@components/advancement/Financial';
import Physical from '@components/advancement/Physical';
import Milestones from 'components/advancement/Milestones';
import Tasks from '@components/advancement/Tasks';
import Risk from '@components/atomic/organisms/Risk/Risk';
import Deliverable from '@components/atomic/organisms/Deliverable/Deliverable';
import { getTaskPossibleStatuses, getUsersForResponsible } from 'lib/api_ts/responsibleApi';
// import { OETrans } from 'components/translation/OETrans';
import Assets from 'components/advancement/Assets';

import Overview from 'components/advancement/Overview';
import { UserContext } from 'components/contexts/UserProvider';
import NavTabs from '@components/atomic/molecules/NavTabs/NavTabs';
import { Loading } from '@components/atomic/atoms';
import SubNavTabs from '@components/atomic/molecules/SubTabs/SubTabs';
import { ProjectType } from '@lib/api_ts/projectApi';
import { getStreamsByProjectId } from '@lib/api_ts/overviewApi';
import { useQuery } from '@tanstack/react-query';

interface AdvancementProps {
	project: ProjectType;
	isCoordinator: boolean;
}

const Advancement = ({ project, isCoordinator }: AdvancementProps) => {
	const { isOe, userAccess } = useContext(UserContext);
	const canEdit = isOe || userAccess.isAboveGuest;
	const isProjectWp = useMemo(() => project?.streamConfigurationType === 'WP', [project?.streamConfigurationType]);

	const { data: users } = useQuery({
		queryKey: ['usersForResponsible', project.companyId.toString()],
		queryFn: () => getUsersForResponsible(project.companyId.toString()),
		enabled: !!project.companyId,
	});

	const { data: streams } = useQuery({
		queryKey: ['streams', project.id.toString()],
		queryFn: () => getStreamsByProjectId(project.id.toString()),
		enabled: !!project.id,
	});

	const { data: optionsStatus } = useQuery({
		queryKey: ['oetaskstatus'],
		queryFn: () =>
			getTaskPossibleStatuses().then((taskStatuses) =>
				taskStatuses.map((status) => ({
					value: status.code,
					label: status.name,
				})),
			),
	});

	// TODO: attiva quando pronto Gantt su Overview
	const [activeTab, setActiveTab] = useState('overview');
	useEffect(() => {
		if (project.streamConfigurationType === 'WP') {
			setActiveTab('overview');
		}
		if (project.streamConfigurationType === 'STREAM') {
			setActiveTab('procedurale');
		}
	}, [project.streamConfigurationType]);
	// const [activeTab, setActiveTab] = useState("procedurale");

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	/*   const allDropdownItems = useMemo(
    () => [
      { name: "deliverables", label: "Deliverables" },
      { name: "milestones", label: "Milestones" },
      { name: "tasks", label: "Task" },
      { name: "targets", label: "Target" },
      { name: "assets", label: "Cespiti" },
      { name: "risks", label: "Rischi" },
    ],
    []
  ); */

	// const allDropdownItems = useMemo(() => {
	// 	if (project && project.targets) {
	// 		return [
	// 			{ name: 'deliverables', label: 'Deliverables' },
	// 			{ name: 'milestones', label: 'Milestones' },
	// 			{ name: 'tasks', label: 'Task' },
	// 			...(project.targets.length ? [{ name: 'targets', label: 'Target' }] : []),
	// 			{ name: 'assets', label: 'Cespiti' },
	// 			{ name: 'risks', label: 'Rischi' },
	// 		];
	// 	} else {
	// 		return [];
	// 	}
	// }, [project]);

	// const [dropdownItems, setDropdownItems] = useState(allDropdownItems);

	// useEffect(() => {
	// 	const streamDropdownItems = ['targets', 'risks'];
	// 	if (project.streamConfigurationType === 'STREAM') {
	// 		setDropdownItems(allDropdownItems.filter((item) => streamDropdownItems.includes(item.name)));
	// 	} else {
	// 		setDropdownItems(allDropdownItems);
	// 	}
	// }, [project.streamConfigurationType, allDropdownItems]);

	useEffect(() => {
		const fisicoTabButton = document.querySelector('#uncontrolled-tab-example-tab-fisico');
		if (!fisicoTabButton) return;

		if (fisicoTabButton.querySelector('.show')) {
			fisicoTabButton.classList.add('has-show');
		} else {
			fisicoTabButton.classList.remove('has-show');
		}
	}, [isDropdownOpen]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest('.dropdown') && isDropdownOpen) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener('click', handleClickOutside);
		} else {
			// Se il dropdown è chiuso, rimuovi l'event listener
			document.removeEventListener('click', handleClickOutside);
		}

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isDropdownOpen]);

	// const isSubTabActive = dropdownItems.map((item) => item.name).includes(activeTab);

	const physicalTabItems = [
		{
			eventKey: 'targets',
			title: 'Targets',
			transCode: 'advancement.physical_tab.targets_subtab',
			content: <Physical projectId={project.id} isCoordinator={isCoordinator} />,
		},
		{
			eventKey: 'deliverables',
			title: 'Deliverables',
			transCode: 'advancement.physical_tab.deliverables_subtab',
			content: (
				<Deliverable
					projectId={project.id}
					isCoordinator={isCoordinator}
					streams={streams}
					users={users}
					optionsStatus={optionsStatus}
					isConfig={false}
				/>
			),
			shouldBeVisible: project.streamConfigurationType === 'WP' && (userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)),
		},
		{
			eventKey: 'milestones',
			title: 'Milestones',
			transCode: 'advancement.physical_tab.milestones_subtab',
			content: (
				<Milestones
					projectId={project.id}
					isCoordinator={isCoordinator}
					streams={streams}
					optionsDMStatus={optionsStatus}
					canEdit={canEdit}
					isConfig={false}
				/>
			),
			shouldBeVisible: project.streamConfigurationType === 'WP' && (userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)),
		},
		{
			eventKey: 'tasks',
			title: 'Tasks',
			transCode: 'advancement.physical_tab.tasks_subtab',
			content: (
				<Tasks
					projectId={project.id}
					isCoordinator={isCoordinator}
					allStreams={streams}
					canEdit={canEdit}
					projectStreamConfigurationType={project.streamConfigurationType}
					projectAdvancedConfig={project.advancedConfig}
				/>
			),
			shouldBeVisible: (userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && project?.streamConfigurationType === 'WP',
		},
		{
			eventKey: 'risks',
			title: 'Rischi',
			transCode: 'advancement.physical_tab.risks_subtab',
			content: <Risk projectId={project.id} />,
			shouldBeVisible: userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest),
		},
		{
			eventKey: 'assets',
			title: 'Cespiti',
			transCode: 'advancement.physical_tab.assets_subtab',
			content: <Assets projectId={project.id} />,
			shouldBeVisible: project.streamConfigurationType === 'WP' && (userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)),
		},
	];

	const tabNavItems = [
		{
			eventKey: 'overview',
			title: 'Overview',
			transCode: 'advancement.overview_tab',
			content: <Overview activeKey={activeTab} projectId={project.id} streams={streams} users={users} />,
			shouldBeVisible: project.streamConfigurationType === 'WP',
		},
		{
			eventKey: 'procedurale',
			title: 'Procedurale',
			transCode: 'advancement.procedural_tab',
			content: <Procedural projectId={project.id} projectNoticedId={project.noticeId} isProjectWp={isProjectWp} />,
		},
		{
			eventKey: 'finanziario',
			title: 'Finanziario',
			transCode: 'advancement.financial_tab',
			content: <Financial projectId={project.id} />,
		},
		{
			eventKey: 'fisico',
			title: 'Fisico',
			transCode: 'advancement.physical_tab',
			content: (
				<div>
					<SubNavTabs subNavItems={physicalTabItems} />
				</div>
			),
		},
	];

	// return !project && project.streamConfigurationType === undefined ?
	return !project && project.streamConfigurationType === undefined ? (
		<Loading fixedHeight="50vh" />
	) : (
		// <div className="d-flex justify-content-center align-items-center" style={{ height: '48vh' }}>
		// 	<div className="spinner-border" role="status">
		// 		<span className="visually-hidden">
		// 			<OETrans type="propTrans" code="common.loading" fallback={'Caricamento'} />
		// 			...
		// 		</span>
		// 	</div>
		// </div>
		<>
			<NavTabs tabNavItems={tabNavItems} />

			{/* <div className="candidacy simple-box scenaritab">
				<Row>
					<Tabs
						variant="pills"
						// defaultActiveKey="procedurale"
						activeKey={activeTab}
						onSelect={(key) => setActiveTab(key)}
						id="uncontrolled-tab-example"
						className="mb-3"
					>
						{project.streamConfigurationType === 'WP' ? (
							<Tab eventKey="overview" title="Overview">
								{project?.id && <Overview activeKey={activeTab} projectId={project.id} streams={streams} users={users} />}
							</Tab>
						) : null}
						<Tab eventKey="procedurale" title="Procedurale">
							{project?.id && (
								<Procedural
									projectId={project.id}
									projectNoticedId={project.noticeId}
									isProjectWp={isProjectWp}
									isBeneficiary={isBeneficiary}
								/>
							)}
						</Tab>
						<Tab eventKey="finanziario" title="Finanziario">
							{project?.id && <Financial projectId={project.id} />}
						</Tab>
						<Tab
							eventKey="fisico"
							title={
								// <div className="d-flex align-items-center gap-2">
								// <div style={{ paddingLeft: "25px" }}>Fisico</div>
								<Dropdown show={isDropdownOpen}>
									<Dropdown.Toggle
										onClick={(event) => {
											event.stopPropagation();
											setIsDropdownOpen((prevIsDropdownOpen) => !prevIsDropdownOpen);
										}}
										as="div"
										className={`border-0 ${isSubTabActive ? 'sub-tab-active' : ''}`.trim()}
										style={{
											textTransform: 'none',
											fontWeight: '400',
											fontSize: '20px',
											padding: '10px 25px',
											borderRadius: '100px',
											// paddingLeft: "0",
										}}
										// />
									>
										Fisico
										{`${isSubTabActive ? ' - ' : ''}${dropdownItems.filter((item) => item.name === activeTab).map((item) => item.label)}`}
									</Dropdown.Toggle>

									<Dropdown.Menu>
										{dropdownItems.map((item) => (
											<Dropdown.Item
												key={item.name}
												eventKey={item.name}
												onClick={(event) => {
													event.stopPropagation();
													setIsDropdownOpen(false);
													setActiveTab(item.name);
												}}
												style={{ borderRadius: '12px', fontSize: '20px' }}
												className="my-2"
											>
												{item.label}
											</Dropdown.Item>
										))}
									</Dropdown.Menu>
								</Dropdown>
								// </div>
							}
						/>
						<Tab.Pane
							eventKey="targets"
							// title="Fisico"
						>
							{project?.id && <Physical projectId={project.id} isCoordinator={isCoordinator} />}
						</Tab.Pane>
						{(userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && (
							<Tab.Pane
								eventKey="deliverables"
								// title="Deliverables"
							>
								{project?.id && streams && (
									<Deliverable
										projectId={project.id}
										streams={streams}
										users={users}
										optionsStatus={optionsStatus}
										isCoordinator={isCoordinator}
									/>
								)}
							</Tab.Pane>
						)}
						{(userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && (
							<Tab.Pane
								eventKey="milestones"
								//  title="Milestones"
							>
								{project?.id && streams && (
									<Milestones
										projectId={project.id}
										canEdit={canEdit}
										streams={streams}
										users={users}
										optionsDMStatus={optionsStatus}
										isCoordinator={isCoordinator}
									/>
								)}
							</Tab.Pane>
						)}
						{(userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && project?.streamConfigurationType === 'WP' ? (
							<Tab.Pane eventKey="tasks">
								{project?.id && streams && (
									<Tasks
										projectId={project.id}
										canEdit={canEdit}
										allStreams={streams}
										users={users}
										projectStreamConfigurationType={project.streamConfigurationType}
										projectAdvancedConfig={project.advancedConfig}
										isCoordinator={isCoordinator}
									/>
								)}
							</Tab.Pane>
						) : null}
						{(userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && (
							<Tab.Pane
								eventKey="risks"
								// title="Rischi"
							>
								{project?.id && <Risk projectId={project.id} isCoordinator={isCoordinator} isBeneficiary={isBeneficiary} />}
							</Tab.Pane>
						)}
						{(userAccess.isAboveGuest || (isCoordinator && userAccess.isGuest)) && (
							<Tab.Pane
								eventKey="assets"
								// title="Cespiti"
							>
								{project?.id && <Assets projectId={project.id} />}
							</Tab.Pane>
						)}
					</Tabs>
				</Row>
			</div> */}
		</>
	);
};
export default Advancement;
